import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Collapse,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { buildAppConfigFromGate0Form } from "../../utils/gate0ProbeConfig";
import { runSystemConnectivityCheck } from "../../services/readinessClient";
import { isProbeSuccess } from "../../utils/gate0Storage";
import {
  authTypeNeedsCredentials,
  normalizeAuthType,
} from "../../utils/apiSurfaceForm";
import {
  endpointResultMatches,
  endpointProbeIsCurrent,
  mergeProbeReports,
  clearProbeResultsForEndpoints,
  probeResultKey,
  statusChipColor,
  tagProbeReportWithForm,
} from "../../utils/probeDisplayUtils";
import { probeOutcomeDisplayItems } from "../../utils/probeOutcomeDisplay";
import ApiSurfaceDeclarationForm from "./ApiSurfaceDeclarationForm";
import RemoveDetailsButton from "./RemoveDetailsButton";

const F = INTAKE_FONT;
const PRIMARY = "#005BF0";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 14,
    fontFamily: F,
    bgcolor: "#fff",
    borderRadius: "4px",
  },
};

const helperSx = {
  fontSize: 12,
  color: INTAKE_COLORS.g5,
  fontFamily: F,
  lineHeight: 1.45,
};

/**
 * Gate 1 API surface — declaration form + Figma credentials/actions card.
 */
export default function Gate1ApiSurfacePanel({
  projectId,
  appName,
  apiForm,
  onApiFormChange,
  credentials,
  onCredentialsChange,
  onSaveDeclaration,
  savingDeclaration = false,
  declarationSaved = false,
  onRemoveDetails,
  removingDetails = false,
  connectionReport,
  onConnectionReport,
  readOnly = false,
  errors = null,
  baseUrlInputRef = null,
}) {
  const [showCredentials, setShowCredentials] = useState(true);
  const [probeRunning, setProbeRunning] = useState(false);
  const [probingEndpointIds, setProbingEndpointIds] = useState([]);
  const [probeError, setProbeError] = useState("");
  const [lastProbeReport, setLastProbeReport] = useState(connectionReport);
  const [expandedResponseId, setExpandedResponseId] = useState(null);
  const [probePersistNote, setProbePersistNote] = useState("");

  const declaredAuth = normalizeAuthType(apiForm.authType);

  const canTestApi =
    Boolean(String(apiForm.baseUrl || "").trim()) &&
    Array.isArray(apiForm.endpoints) &&
    apiForm.endpoints.some((ep) => String(ep?.path || "").trim());
  const canRemoveDetails =
    canTestApi ||
    Boolean(String(apiForm.authType || "").trim()) ||
    Boolean(connectionReport) ||
    declarationSaved;

  useEffect(() => {
    if (authTypeNeedsCredentials(apiForm.authType)) {
      setShowCredentials(true);
    }
  }, [apiForm.authType]);

  useEffect(() => {
    if (connectionReport && !probeRunning) {
      setLastProbeReport(connectionReport);
    } else if (!connectionReport && !probeRunning) {
      setLastProbeReport(null);
      setProbeError("");
      setProbePersistNote("");
    }
  }, [connectionReport, probeRunning]);

  const runProbe = async (endpointIds) => {
    const ids = Array.isArray(endpointIds) && endpointIds.length ? endpointIds : [];
    const built = buildAppConfigFromGate0Form(
      { ...apiForm, appName: appName || "AI System" },
      credentials,
      { endpointIds: ids.length ? ids : undefined }
    );
    if (built.error) {
      setProbeError(built.error);
      setShowCredentials(true);
      return;
    }

    try {
      setProbeRunning(true);
      setProbingEndpointIds(ids);
      setProbeError("");
      setProbePersistNote("");

      if (onSaveDeclaration) {
        try {
          await onSaveDeclaration();
        } catch {
          /* save must not skip POST /readiness/system-check */
        }
      }

      const report = await runSystemConnectivityCheck(built.app_config, {
        endpointIds: ids.length ? ids : undefined,
        projectId,
        gatePhase: "gate1",
      });

      const clearedPrev = clearProbeResultsForEndpoints(
        lastProbeReport,
        ids.length ? ids : null,
        apiForm.endpoints
      );

      const merged = tagProbeReportWithForm(
        mergeProbeReports(
          clearedPrev,
          report,
          ids.length ? ids : undefined,
          apiForm.endpoints
        ),
        apiForm,
        ids.length ? ids : undefined
      );
      setLastProbeReport(merged);
      onConnectionReport?.(merged);

      if (report.persisted?.saved) {
        setProbePersistNote("Results saved to database for risk assessment.");
      } else if (report.persisted && !report.persisted.saved && isProbeSuccess(report)) {
        setProbePersistNote(`Results not saved (${report.persisted.reason || "unknown"}).`);
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.hint ||
        err.response?.data?.message ||
        err.message ||
        "API test failed.";
      setProbeError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setProbeRunning(false);
      setProbingEndpointIds([]);
    }
  };

  const reportForDisplay = lastProbeReport;

  const bearerValue =
    String(credentials.bearerToken || "").trim().toLowerCase() === "bearer token"
      ? ""
      : credentials.bearerToken || "";

  const testAllLink = !readOnly ? (
    <Box
      component="button"
      type="button"
      disabled={!canTestApi || probeRunning}
      onClick={() => runProbe()}
      sx={{
        border: "none",
        bgcolor: "transparent",
        color: !canTestApi || probeRunning ? INTAKE_COLORS.g3 : PRIMARY,
        fontSize: 13,
        fontWeight: 700,
        fontFamily: F,
        cursor: !canTestApi || probeRunning ? "default" : "pointer",
        p: 0,
        textDecoration: "none",
        whiteSpace: "nowrap",
        "&:hover": {
          textDecoration: !canTestApi || probeRunning ? "none" : "underline",
        },
      }}
    >
      {probeRunning ? "Testing…" : "Test all endpoints"}
    </Box>
  ) : null;

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <ApiSurfaceDeclarationForm
        value={apiForm}
        onChange={readOnly ? () => {} : onApiFormChange}
        showIntro
        readOnly={readOnly}
        errors={errors}
        baseUrlInputRef={baseUrlInputRef}
        endpointsHeaderActions={testAllLink}
        renderEndpointFooter={(ep) => {
          const matchedResults = (reportForDisplay?.endpoints || []).filter((r) =>
            endpointResultMatches(ep, r)
          );
          const probeResults = matchedResults.filter((r) =>
            endpointProbeIsCurrent(r, ep, apiForm)
          );
          const staleResults = matchedResults.filter((r) => !probeResults.includes(r));
          const previousResult = staleResults[0] || null;
          const previousLabel = [previousResult?.method, previousResult?.path]
            .map((part) => String(part || "").trim())
            .filter(Boolean)
            .join(" ");
          const isProbingThis =
            probingEndpointIds.length > 0 &&
            probingEndpointIds.some(
              (id) => String(id) === String(ep.id)
            );
          return (
            <>
              {!readOnly && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${INTAKE_COLORS.g2}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={!canTestApi || probeRunning}
                    onClick={() => runProbe([ep.id])}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: F,
                      bgcolor: PRIMARY,
                      borderRadius: "4px",
                      "&:hover": { bgcolor: "#0046B8" },
                    }}
                  >
                    {probeRunning ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      "Test this Endpoint"
                    )}
                  </Button>
                </Box>
              )}

              {previousResult && probeResults.length === 0 && (
                <Alert severity="info" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
                  Previous {previousLabel || "endpoint"} result is stale and is not shown as the
                  current endpoint result. Run a new test for this endpoint.
                </Alert>
              )}

              {isProbingThis && probeResults.length === 0 && (
                <Typography sx={{ mt: 1.5, fontSize: 12, color: INTAKE_COLORS.g5, fontFamily: F }}>
                  Running live check…
                </Typography>
              )}

              {probeResults.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  {probeResults.map((probeResult) => {
                    const resultKey = probeResultKey(
                      probeResult.endpoint_id || ep.id,
                      probeResult.payload_name
                    );
                    const chipColor = statusChipColor(probeResult.status_code);
                    const statusText =
                      probeResult.reachable === false
                        ? "Unreachable"
                        : probeResult.status_code != null
                          ? `HTTP ${probeResult.status_code}`
                          : "Checked";
                    return (
                      <Box
                        key={resultKey}
                        sx={{
                          mt: 1,
                          p: 1.25,
                          borderRadius: "6px",
                          bgcolor: "#fff",
                          border: `1px solid ${INTAKE_COLORS.g2}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ mb: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={statusText}
                            sx={{ height: 22, fontWeight: 700, bgcolor: chipColor, color: "#fff" }}
                          />
                          {probeOutcomeDisplayItems(probeResult).map((item) => (
                            <Chip
                              key={item.key}
                              size="small"
                              label={`${item.title} ${item.text}`}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontFamily: F,
                                bgcolor: item.tone === "pass" ? "#E8FCF3" : "#FFD2C7",
                                color: item.tone === "pass" ? "#00754E" : "#B82500",
                              }}
                            />
                          ))}
                          {probeResult.latency_ms != null && (
                            <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.g5, fontFamily: F }}>
                              {probeResult.latency_ms}ms
                            </Typography>
                          )}
                          <Box
                            component="button"
                            type="button"
                            onClick={() =>
                              setExpandedResponseId((cur) => (cur === resultKey ? null : resultKey))
                            }
                            sx={{
                              border: "none",
                              bgcolor: "transparent",
                              color: PRIMARY,
                              fontSize: 12,
                              fontWeight: 700,
                              fontFamily: F,
                              cursor: "pointer",
                              p: 0,
                              ml: "auto",
                            }}
                          >
                            {expandedResponseId === resultKey ? "Hide response" : "View response"}
                          </Box>
                        </Stack>
                        {(probeResult.flags || []).length > 0 && (
                          <Stack spacing={0.25} sx={{ mb: 0.5 }}>
                            {(probeResult.flags || []).slice(0, 4).map((flag, i) => (
                              <Typography
                                key={i}
                                sx={{ fontSize: 11, color: INTAKE_COLORS.g5, fontFamily: F }}
                              >
                                {flag.message}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                        <Collapse in={expandedResponseId === resultKey}>
                          {probeResult.response_from && (
                            <Typography
                              sx={{ fontSize: 11, color: INTAKE_COLORS.g5, mb: 0.5, fontFamily: F }}
                            >
                              Response from: {String(probeResult.response_from).replace(/_/g, " ")}
                              {probeResult.response_content_type
                                ? ` · ${probeResult.response_content_type}`
                                : ""}
                            </Typography>
                          )}
                          {probeResult.request_body_preview && (
                            <>
                              <Typography
                                sx={{ fontSize: 11, color: INTAKE_COLORS.g5, mb: 0.5, fontFamily: F }}
                              >
                                Request body
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  mb: 1,
                                  p: 1.25,
                                  fontSize: 11,
                                  fontFamily: "monospace",
                                  bgcolor: "#f6f8fa",
                                  borderRadius: "4px",
                                  overflow: "auto",
                                  maxWidth: "100%",
                                  maxHeight: 120,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {probeResult.request_body_preview}
                              </Box>
                            </>
                          )}
                          <Typography
                            sx={{ fontSize: 11, color: INTAKE_COLORS.g5, mb: 0.5, fontFamily: F }}
                          >
                            Response body
                          </Typography>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1.25,
                              fontSize: 11,
                              fontFamily: "monospace",
                              bgcolor: "#0d1117",
                              color: "#e6edf3",
                              borderRadius: "4px",
                              overflow: "auto",
                              maxWidth: "100%",
                              maxHeight: 220,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {probeResult.response_body_preview ||
                              probeResult.notes ||
                              "(no response captured)"}
                            {probeResult.response_body_truncated ? "\n\n… (truncated)" : ""}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </>
          );
        }}
      />

      {/* Credentials + actions card (Figma) */}
      {!readOnly && (
        <Box
          sx={{
            mt: 2.5,
            p: { xs: 2, sm: 2.5 },
            border: `1px solid ${INTAKE_COLORS.g2}`,
            borderRadius: "8px",
            bgcolor: "#fff",
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => setShowCredentials((v) => !v)}
            sx={{
              display: "block",
              border: "none",
              bgcolor: "transparent",
              color: INTAKE_COLORS.g5,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: F,
              cursor: "pointer",
              p: 0,
              mb: showCredentials ? 1.5 : 0,
              textAlign: "left",
              "&:hover": { color: "#383838" },
            }}
          >
            {showCredentials ? "Hide test credentials" : "Show test credentials"}
          </Box>

          <Collapse in={showCredentials}>
            <Stack spacing={1.5} sx={{ width: "100%", mb: 1.5 }}>
              <Box sx={{ width: "100%" }}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  placeholder="Bearer token"
                  value={bearerValue}
                  onChange={(e) =>
                    onCredentialsChange({ ...credentials, bearerToken: e.target.value })
                  }
                  inputProps={{ autoComplete: "new-password", name: "api-bearer-token" }}
                  sx={inputSx}
                />
                <Typography sx={{ ...helperSx, mt: 0.75 }}>
                  Paste the token only — do not include the Bearer prefix.
                </Typography>
                <Typography sx={{ ...helperSx, mt: 0.25 }}>
                  Credentials are used for the live test and are not stored with the project.
                </Typography>
              </Box>

              {declaredAuth.includes("basic") && (
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Basic auth email or username"
                    value={credentials.basicEmail || ""}
                    onChange={(e) =>
                      onCredentialsChange({ ...credentials, basicEmail: e.target.value })
                    }
                    inputProps={{ autoComplete: "off", name: "api-basic-email" }}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    placeholder="Basic auth API token / PAT"
                    value={credentials.basicApiToken || ""}
                    onChange={(e) =>
                      onCredentialsChange({ ...credentials, basicApiToken: e.target.value })
                    }
                    inputProps={{ autoComplete: "new-password", name: "api-basic-token" }}
                    sx={inputSx}
                  />
                </Stack>
              )}

              {declaredAuth.includes("api key") && (
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="API key header (e.g. X-API-Key)"
                    value={credentials.apiKeyHeader || ""}
                    onChange={(e) =>
                      onCredentialsChange({ ...credentials, apiKeyHeader: e.target.value })
                    }
                    inputProps={{ autoComplete: "off", name: "api-key-header" }}
                    sx={inputSx}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    placeholder="API key value"
                    value={credentials.apiKeyValue || ""}
                    onChange={(e) =>
                      onCredentialsChange({ ...credentials, apiKeyValue: e.target.value })
                    }
                    inputProps={{ autoComplete: "new-password", name: "api-key-value" }}
                    sx={inputSx}
                  />
                </Stack>
              )}
            </Stack>
          </Collapse>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              mt: showCredentials ? 0 : 2,
            }}
          >
            <Button
              variant="outlined"
              disableElevation
              disabled={savingDeclaration}
              onClick={() => onSaveDeclaration?.()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontFamily: F,
                fontSize: 14,
                px: 2,
                py: 0.75,
                borderRadius: "4px",
                borderColor: PRIMARY,
                color: PRIMARY,
                bgcolor: "#fff",
                "&:hover": { borderColor: "#0046B8", bgcolor: "#F0F6FF" },
              }}
            >
              {savingDeclaration ? "Saving…" : "Save API declaration"}
            </Button>
            {onRemoveDetails ? (
              <RemoveDetailsButton
                onClick={() => onRemoveDetails()}
                disabled={!canRemoveDetails || savingDeclaration}
                removing={removingDetails}
                sx={{ height: 36, fontSize: 14, fontWeight: 600 }}
              />
            ) : null}
            {declarationSaved && (
              <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.green, fontFamily: F }}>
                Saved
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {probeError && (
        <Alert severity="warning" sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}>
          {probeError}
        </Alert>
      )}
      {reportForDisplay && !probeError && reportForDisplay.generated_at && (
        <Alert
          severity={isProbeSuccess(reportForDisplay) ? "success" : "info"}
          sx={{ mt: 1.5, fontSize: 13, fontFamily: F }}
        >
          API test complete
          {reportForDisplay.probe_mode === "api_agent" ? " (apiAgent)" : ""}
          {probePersistNote ? ` — ${probePersistNote}` : " — results available for risk assessment."}
          {reportForDisplay.generated_at
            ? ` · Last run: ${new Date(reportForDisplay.generated_at).toLocaleString()}`
            : ""}
        </Alert>
      )}
    </Box>
  );
}
